import Box from "@mui/material/Box";
import { IMPACT_REPORT_FALLBACK_IMAGE_SRC } from "@/constants/impactReport";
import type { ImpactReportCardRow } from "@/interfaces/impactReportView";

interface ImpactCardProps {
  /**
   * The row as the flatten produced it — the wire's `ImpactTypeStat` paired with its already-read
   * narrative register.
   *
   * The card takes the ROW rather than the raw stat so the register read happens exactly once,
   * at flatten time, where `utils/impact-report-helper.ts` owns the contract's three states. A
   * component that re-derived it would be a second place that has to keep branching on
   * `suppressed` rather than on an empty string, which is precisely the mistake the union in
   * `interfaces/impactReportView.ts` exists to make unrepresentable.
   */
  row: ImpactReportCardRow;
}

/**
 * ONE tile in the "Explore your Impact" grid: a portrait card with the photograph across the top
 * two-thirds, the narrative figure and its label immediately beneath, and the wire's sentence
 * filling the remainder.
 *
 * FRONT FACE ONLY — a decided scope cut, not an unfinished component. The prototype
 * (`renderRpImpact()`'s card branch) builds a 3D card with a second face behind the photo, an
 * info button that turns it over, and a back panel naming a partner, a rationale and a title.
 * None of those three fields exists anywhere in the contract: `ImpactTypeStat` carries
 * `impactTypeName`, `cardLabel`, `referenceValueId`, `totalQuantity`, `statements` and `image`,
 * and nothing else. Building that panel would mean Platform inventing copy about a client's
 * charitable partners in the browser. So there is no second face, no 3D rotation, no info
 * affordance, no click handler and no pointer cursor: this is a static, non-interactive tile.
 *
 * Every word of impact language on it arrives already worded. Nothing here templates a sentence,
 * substitutes into one, appends a unit or a quantity to one, or re-cases one — the prototype's
 * substitution of a formatted figure into a placeholder inside its own statement string is deleted
 * rather than ported, because the contract ships the sentence with the quantity already inside it.
 *
 * Styled with MUI `sx` over the design tokens in `index.css`, reproducing the prototype's
 * PROPORTIONS rather than its CSS: its `.ir-*` rules belong to a standalone HTML file with a
 * different cascade, and this card has no stylesheet of its own to be overridden by.
 */
export function ImpactCard({ row }: ImpactCardProps) {
  const { impactType, narrative } = row;

  // `image` is `ReportImage | null` as a WHOLE FIELD — the contract never ships an object with a
  // null url — so the test is on the field, not on `image.url`.
  //
  // The `source` field on `ReportImage` is read NOWHERE. It is provenance — which rung of the
  // server's own resolution produced this photograph — not a rendering instruction; branching on
  // it here would rebuild client-side the source ladder GFG-540 deliberately removed upstream.
  const imageSrc =
    impactType.image === null ? IMPACT_REPORT_FALLBACK_IMAGE_SRC : impactType.image.url;

  // Absent from an unauthored card by construction: the union has no `sentence` on that variant,
  // so there is nothing to accidentally print. A WITHHELD card still has one — it carries the
  // fallback phrase Crunchy already chose, and Platform never selects between copy variants.
  const sentence = narrative.kind === "unauthored" ? "" : narrative.sentence;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        // NO fixed aspect ratio on the tile. The prototype's 5:6 portrait derived the card's
        // HEIGHT from its width, which made the body a fixed third of it — at the 3-column
        // breakpoint that left roughly one and a half lines for a sentence the wire routinely
        // ships at 90–120 characters, silently cutting it mid-word with no ellipsis. The
        // narrative sentence is the point of the tile and the slice requires it verbatim, so the
        // proportion is fixed on the PHOTO instead (5:4 below = the same two-thirds of a 5:6
        // tile) and the body grows with its copy. Wherever the grid has more than one column, it
        // stretches every card to the height of the tallest card anywhere in the grid (see
        // `ImpactCardGrid`'s `gridAutoRows: "1fr"`) — stretching a card's shorter neighbours to
        // match it is the point, not a bug to guard against. The photo just below stays
        // `flex: "0 0 auto"` at its fixed 5:4 ratio regardless, so that extra height can only
        // ever land in the body beneath the sentence; it never stretches or crops the
        // photograph. At one column each card is its own row with no neighbour to match, so the
        // grid drops the shared height there and every card is free to size to its own copy.
        overflow: "hidden",
        borderRadius: "var(--radius-card)",
        border: "1px solid var(--color-smoke)",
        backgroundColor: "var(--color-white)",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
      }}
    >
      {/* Photo: the prototype's top two-thirds, expressed as its own 5:4 box so its height comes
          from the card's WIDTH and never competes with the sentence for vertical room. On a wax
          ground so a transparent or still-loading image does not flash the card's white body
          through. */}
      <Box
        sx={{
          flex: "0 0 auto",
          aspectRatio: "5 / 4",
          position: "relative",
          backgroundColor: "var(--color-wax)",
        }}
      >
        <Box
          component="img"
          src={imageSrc}
          /* Decorative: the label directly beneath already names this impact type, so describing
             the photograph again would have a screen reader announce the same words twice. There
             is also no authored description on the wire, and composing one here would be Platform
             writing impact language. */
          alt=""
          aria-hidden="true"
          loading="lazy"
          onError={(event) => {
            const failedSrc = event.currentTarget.getAttribute("src");
            // RE-ENTRY GUARD. Crunchy's urls point at externally-hosted photography that can 404,
            // and swapping the bundled fallback in for itself would fire this handler again on
            // every failure — an unbounded loop that pins a core, multiplied by every tile in the
            // grid. The swap therefore happens at most once per card.
            if (failedSrc === IMPACT_REPORT_FALLBACK_IMAGE_SRC) {
              console.error(
                "ImpactCard: the bundled fallback image itself failed to load; the card renders without a photograph",
                {
                  impactTypeName: impactType.impactTypeName,
                  src: IMPACT_REPORT_FALLBACK_IMAGE_SRC,
                },
              );
              return;
            }
            console.error("ImpactCard: impact type image failed to load, using bundled fallback", {
              impactTypeName: impactType.impactTypeName,
              src: failedSrc,
            });
            event.currentTarget.src = IMPACT_REPORT_FALLBACK_IMAGE_SRC;
          }}
          sx={{
            display: "block",
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: 0.9,
          }}
        />
      </Box>

      {/* Body: figure, label, sentence. */}
      <Box
        sx={{
          flex: "1 1 auto",
          display: "flex",
          flexDirection: "column",
          padding: "16px 20px",
        }}
      >
        {/* The large slot exists ONLY on a rendered card. A withheld or unauthored one emits NO
            element at all rather than an empty one: an empty div still occupies its line-height,
            leaving a gap above the label that reads as an image that never loaded.

            The union does HALF of that work: `formattedQuantity` is unreachable on the withheld
            and unauthored variants, so neither can reach this slot at all. It does NOT do the
            other half — on `rendered`, `formattedQuantity` is a plain `string`, so an authored
            statement whose quantity formatter resolved nothing (`formattedQuantity: ""`) is
            perfectly representable and would set the empty string at 44px. The `!== ""` test below
            is what collapses the slot in that state, matching `composeStatisticValue`'s rule for
            the same wire shape on the hero side (`utils/impact-report-helper.ts`), which returns
            `null` for an empty composed value so the renderer omits the element rather than
            reserving its vertical space.

            NOTE this is not the below-floor state: an unformatted-but-authored quantity is a
            different fact from `withheld` (the server holding a figure back under this metric's
            own display floor), so the helper is right not to relabel it. */}
        {narrative.kind === "rendered" && narrative.formattedQuantity !== "" && (
          <Box
            sx={{
              fontFamily: "var(--font-crimson)",
              fontSize: "44px",
              fontWeight: 400,
              lineHeight: 1.05,
              color: "var(--color-rodeo-black)",
            }}
          >
            {narrative.formattedQuantity}
          </Box>
        )}

        {/* The label names the unit ("Minutes of Employment"), which is why the figure above is
            rendered alone and the statement's own `unit` is never appended to it.

            `--color-dust-ink`, NOT the prototype's `--color-rodeo-dust`: index.css documents that
            token at roughly 2.8:1 as text and names dust-ink as the text-safe dust. The repo's own
            written accessibility rule outranks the prototype's pixel value — same hue family, an
            intentional half-shade darker. dust-ink clears AA on this card's white ground (4.83:1);
            it would NOT on a light-dust one, which is why the tile is white.

            This is `cardLabel`, not `impactTypeName` — the authored heading a human reads, not
            the stored metric identity the sort and cross-payload correlation key on. The field is
            non-nullable on the contract, with a server-side fallback to `impactTypeName` when
            nothing was authored.

            The local fallback is NOT a second source of truth for the label — it is a floor for
            the two states the declared type does not actually exclude, both of which render the
            heading EMPTY: silent, and in a document a client forwards onward.

            `||`, DELIBERATELY NOT `??`. The schema is `z.string()` with no `.min(1)`, so `''` is
            contract-legal, and it is a state the admin form really produces — Crunchy's
            `resolveMetricCardLabel` treats `''` as unauthored precisely because "the admin form
            submits the field whether or not it was filled in, so an empty string is what 'cleared
            it' looks like on the way to the column", and its own comment warns that a `??` there
            "would let that reach the wire as a card with no heading at all, a defect no consumer
            could distinguish from a rendering bug." The same reasoning applies one layer down:
            `??` covers null/undefined and sails straight past `''`. `||` mirrors the server's
            three conditions exactly.

            The second state is version skew — a Crunchy deployed before `cardLabel` shipped
            (25 Aug), reached by a rollback or a lagging environment. Nothing catches that
            upstream: `describeImpactReportEnvelopeDefect` validates only `meta`, `meta.warnings`,
            `organizationId` and `data` and never walks `causeData[].impactTypes[]`, so such a
            payload passes validation, is cast to `ImpactReport`, and the declared `string` type
            becomes a runtime lie.

            Resolving to the same value the server would have chosen makes this a no-op whenever
            the contract holds.

            (Not a cache concern, despite what the `thankYou` note elsewhere in this feature implies:
            Crunchy composes every report fresh — `meta.cacheHit` is hardcoded `false` and no call
            site sets it. There are no replayed payloads, only older deployments.)

            Cased by CSS, never by JS: the label is rendered as authored. */}
        <Box
          sx={{
            marginTop: "5px",
            fontSize: "11px",
            fontWeight: 700,
            letterSpacing: "2px",
            textTransform: "uppercase",
            color: "var(--color-dust-ink)",
          }}
        >
          {impactType.cardLabel || impactType.impactTypeName}
        </Box>

        {/* The wire's sentence, verbatim, in a text node of its own. `""` is the wire's own
            explicitly-absent value (no fallback phrase was authored), so the element is omitted
            rather than emitted empty — the same convention the figure above uses.

            NOT clipped and NOT clamped: no `overflow: hidden`, no fixed height, no line count.
            The slice requires the sentence verbatim, so the element takes exactly as many lines
            as its copy needs at any viewport width and the card grows to contain them. */}
        {sentence !== "" && (
          <Box
            sx={{
              marginTop: "10px",
              fontSize: "13px",
              lineHeight: 1.5,
              color: "var(--color-graphite)",
            }}
          >
            {sentence}
          </Box>
        )}
      </Box>
    </Box>
  );
}
